/**
 * The document-scanner seam. WF9's extraction accuracy depends on capture
 * quality, so on Android the native ML Kit document scanner (auto edge-detect,
 * crop, deskew, multi-page) is worth far more than a raw camera frame — but it
 * exists only behind a Capacitor plugin, and only on Android (Google never
 * shipped it for iOS; the plugin's own Swift implementation is
 * `rejectCallAsUnimplemented`, so iOS installs it cleanly and always falls
 * through to the file input. A VisionKit-backed implementation behind THIS same
 * function is the iOS upgrade path).
 *
 * Callers use exactly one entry point and fall back to the file-input path
 * when it returns null — the web build, dev servers, and any device where the
 * plugin is missing all take the fallback with no further branching.
 */
import { registerPlugin } from "@capacitor/core";

export interface ScannedPage {
  blob: Blob;
  filename: string;
}

/** Why the native path was unavailable, so the UI can say something true.
 *  "ready" means it ran; everything else routes to the camera input. */
export type ScannerAvailability =
  | "ready"
  | "installing"
  | "unsupported-platform"
  | "unavailable";

declare global {
  interface Window {
    Capacitor?: {
      isNativePlatform?: () => boolean;
      getPlatform?: () => string;
      /** Rewrites a native file://|content:// URI into one the WebView may
       *  actually read. Identity on web, so it is safe to call unconditionally. */
      convertFileSrc?: (path: string) => string;
    };
  }
}

export function isNativeApp(): boolean {
  try {
    return window.Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

export type NativePlatform = "android" | "ios" | "web";

/** Which shell we are in. The ONLY sanctioned platform switch — never sniff
 *  the user agent; both shells' WebViews lie about it. */
export function nativePlatform(): NativePlatform {
  if (!isNativeApp()) return "web";
  const p = window.Capacitor?.getPlatform?.();
  return p === "ios" ? "ios" : "android";
}

/**
 * A native URI the WebView can actually fetch.
 *
 * LOAD-BEARING. The scanner hands back `file://…` or `content://…`, and the
 * WebView's origin is `https://localhost`: the Fetch API cannot read either
 * scheme and throws. Without this the throw is swallowed by the catch below,
 * `scanDocument` returns null, and the app silently drops to the file input —
 * so the scanner ships, appears to do nothing, and reports no error anywhere.
 */
function readableUri(uri: string): string {
  try {
    return window.Capacitor?.convertFileSrc?.(uri) ?? uri;
  } catch {
    return uri;
  }
}

/** The slice of the plugin's surface we use. Typed rather than `any` because
 *  it IS the contract with a module the bundler never resolves — if the plugin
 *  changes shape, nothing else would tell us. */
interface ScannerPlugin {
  DocumentScanner?: {
    scanDocument(options: {
      pageLimit?: number;
      galleryImportAllowed?: boolean;
      resultFormats?: "JPEG" | "PDF" | "JPEG_PDF";
      scannerMode?: "FULL" | "BASE" | "BASE_WITH_FILTER";
    }): Promise<{ scannedImages?: string[] }>;
    isGoogleDocumentScannerModuleAvailable(): Promise<{ available: boolean }>;
    installGoogleDocumentScannerModule(): Promise<void>;
  };
}

/**
 * The plugin, or null.
 *
 * Obtained through `registerPlugin` from `@capacitor/core` — NOT by importing
 * `@capacitor-mlkit/document-scanner`. That distinction is the whole reason
 * scanning works at all.
 *
 * The previous version held the package name in a variable and did
 * `await import(MLKIT_MODULE)` with `@vite-ignore`, to keep the plugin out of
 * the web bundle. It succeeded at that and thereby broke the feature: a bare
 * module specifier cannot be resolved by a runtime `import()` in a WebView —
 * there is no import map — so the call threw on EVERY platform, the blanket
 * catch returned null, and Android fell through to the file picker exactly as
 * the web does. The APK shipped a scanner that could never run, with no error
 * anywhere and the Gradle dependency compiled in to make it look present.
 *
 * `registerPlugin` is Capacitor's own answer to this: it returns a proxy bound
 * to the NATIVE implementation by name (`@CapacitorPlugin(name =
 * "DocumentScanner")`), pulls no plugin JS into the bundle, and on a platform
 * with no native side simply rejects — which the callers below already treat as
 * "take the fallback".
 */
const DocumentScanner = registerPlugin<ScannerPlugin["DocumentScanner"]>("DocumentScanner");

async function loadPlugin(): Promise<ScannerPlugin | null> {
  // Native only. On web the proxy exists but every call rejects, and asking is
  // slower and noisier than knowing.
  if (!isNativeApp()) return null;
  return { DocumentScanner };
}

/**
 * Make sure the scanner can run, and start its download if it cannot yet.
 *
 * ML Kit's document scanner is a Google Play **dynamic module**: it is not on
 * every device, and `scanDocument` simply rejects when it is absent. Call this
 * once when the capture screen mounts so the few megabytes arrive over whatever
 * wifi exists at sign-in — not while an operator is standing over a wet sheet
 * with the line still running.
 *
 * Never block the flow on it. The fallback ladder still works while it installs.
 */
export async function ensureScanner(): Promise<ScannerAvailability> {
  if (!isNativeApp()) return "unavailable";
  if (nativePlatform() === "ios") return "unsupported-platform";
  const plugin = await loadPlugin();
  if (!plugin?.DocumentScanner) return "unavailable";
  try {
    const { available } =
      await plugin.DocumentScanner.isGoogleDocumentScannerModuleAvailable();
    if (available) return "ready";
    await plugin.DocumentScanner.installGoogleDocumentScannerModule();
    return "installing";
  } catch {
    // An older plugin build may not expose the availability probe at all. That
    // is not a reason to refuse to try scanning.
    return "unavailable";
  }
}

/**
 * Open the native scanner and return its pages, or null when the native path is
 * unavailable (use the <input capture> fallback). An empty array means the
 * operator opened it and backed out — which is NOT the same as unavailable, and
 * must not silently reopen a different picker in their face.
 */
export async function scanDocument(maxPages: number): Promise<ScannedPage[] | null> {
  if (!isNativeApp()) return null;
  const plugin = await loadPlugin();
  if (!plugin?.DocumentScanner) return null;
  try {
    const result = await plugin.DocumentScanner.scanDocument({
      pageLimit: maxPages,
      galleryImportAllowed: true,
      resultFormats: "JPEG",
      // BASE, deliberately, and this is not a performance choice.
      //
      // The plugin's default is FULL, documented as "ML-enabled image cleaning
      // capabilities (erase stains, fingers, etc.)". On handwritten production
      // sheets that is a model retouching the evidence before two other models
      // read it — the precise silent corruption this workflow exists to prevent,
      // and it would land upstream of every arithmetic check we rely on.
      //
      // Promote to BASE_WITH_FILTER only on measured evidence from /ocr-testing,
      // where accuracy, recall@k and digit-error-rate already exist.
      scannerMode: "BASE",
    });
    const pages: ScannedPage[] = [];
    for (const [index, uri] of (result?.scannedImages ?? []).entries()) {
      const res = await fetch(readableUri(String(uri)));
      pages.push({ blob: await res.blob(), filename: `scan-${index + 1}.jpg` });
    }
    return pages;
  } catch {
    // Plugin present but the scanner could not run on this device — fallback.
    return null;
  }
}
